import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Share2,
  BarChart3,
  Settings,
  LogOut,
  X,
  FileText,
  ExternalLink,
  Phone,
  Headphones,
  Image,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from './ConfirmDialog';

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/conversations', label: 'Conversations', icon: MessageSquare },
  { to: '/leads', label: 'Leads', icon: Users },
  { to: '/referrals', label: 'Referrals', icon: Share2 },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/promotional-banner', label: 'Promo Banner', icon: Image },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export default function Sidebar({ open, onClose, collapsed, onToggleCollapse }) {
  const { logout, user } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout();
  };

  return (
    <>
      {/* Overlay for mobile */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-40 flex h-full flex-col border-r border-surface-600 bg-surface-900 transition-all duration-200 lg:static lg:translate-x-0 ${
          collapsed ? 'w-16' : 'w-64'
        } ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className={`flex items-center border-b border-surface-600 ${collapsed ? 'justify-center px-2' : 'justify-between px-5'} py-4`}>
          {collapsed ? (
            <img
              src="/favicon.png"
              alt="Dr. Mali Logo"
              className="h-8 w-8 rounded-lg object-contain"
            />
          ) : (
            <div className="flex items-center gap-2.5">
              <img
                src="/favicon.png"
                alt="Dr. Mali Logo"
                className="h-10 w-10 rounded-xl object-contain"
              />
              <div>
                <h1 className="text-base font-bold text-slate-100">Dr. Mali</h1>
                <p className="text-[10px] font-medium uppercase tracking-widest text-neon-cyan/60">
                  Admin Panel
                </p>
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className={`rounded-lg p-1 text-slate-400 hover:bg-surface-700 hover:text-slate-200 lg:hidden ${collapsed ? 'hidden' : ''}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 space-y-1 ${collapsed ? 'px-2' : 'px-3'} py-4`}>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={onClose}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-xl ${collapsed ? 'px-2' : 'px-3'} py-2.5 text-sm font-medium transition-all duration-200 ${isActive
                  ? 'bg-neon-cyan/10 text-neon-cyan shadow-neon-cyan'
                  : 'text-slate-400 hover:bg-surface-700 hover:text-slate-200'
                }`
              }
            >
              <Icon size={18} />
              {!collapsed && label}
            </NavLink>
          ))}

          <div className="my-2 border-t border-surface-600" />

          <a
            href="https://drive.google.com/drive/folders/1RkTxKc3n_lqMwlKHxsDiMfDmAirI-foU?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? 'Reports' : undefined}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-xl ${collapsed ? 'px-2' : 'px-3'} py-2.5 text-sm font-medium text-slate-400 transition-all duration-200 hover:bg-surface-700 hover:text-slate-200`}
          >
            <FileText size={18} />
            {!collapsed && (
              <>
                Reports
                <ExternalLink size={12} className="ml-auto opacity-50" />
              </>
            )}
          </a>
        </nav>

        {/* Support */}
        {!collapsed && (
          <div className="border-t border-surface-600 px-3 py-3">
            <div className="mb-2 flex items-center gap-2 px-3 text-xs font-medium text-slate-500">
              <Headphones size={14} />
              Support
            </div>
            <p className="mb-2 px-3 text-xs text-slate-500">+254 717 811 921</p>
            <a
              href="tel:+254717811921"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-400 transition-all hover:bg-surface-700 hover:text-slate-200"
            >
              <Phone size={16} />
              Call
            </a>
            <a
              href="https://wa.me/254717811921"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-400 transition-all hover:bg-neon-green/10 hover:text-neon-green"
            >
              <MessageSquare size={16} />
              WhatsApp
            </a>
          </div>
        )}

        {/* User + Logout */}
        <div className={`border-t border-surface-600 ${collapsed ? 'px-2' : 'px-4'} py-4`}>
          {!collapsed && (
            <div className="mb-3 rounded-xl bg-surface-700/50 px-3 py-2.5">
              <p className="text-xs font-semibold text-slate-200">{user?.name}</p>
              <p className="text-[11px] text-slate-500">{user?.username}</p>
            </div>
          )}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            title={collapsed ? 'Sign Out' : undefined}
            className={`flex w-full items-center ${collapsed ? 'justify-center' : 'gap-2'} rounded-xl px-3 py-2 text-sm text-slate-400 transition-all hover:bg-neon-pink/10 hover:text-neon-pink`}
          >
            <LogOut size={16} />
            {!collapsed && 'Sign Out'}
          </button>
        </div>

        {/* Collapse Toggle Button (desktop only) */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-20 h-6 w-6 items-center justify-center rounded-full border border-surface-600 bg-surface-800 text-slate-400 hover:bg-surface-700 hover:text-slate-200 transition-all"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      <ConfirmDialog
        open={showLogoutConfirm}
        title="Sign Out"
        message="Are you sure you want to sign out of the admin dashboard?"
        confirmLabel="Sign Out"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </>
  );
}
